import type { ClientPoll } from '@DigitaleDoerfer/digitale-doerfer-api';
import { useCallback, useMemo, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Button } from 'react-native-paper';
import { useSharedValue } from 'react-native-reanimated';

import { ArrowBackIcon, BarChartIcon } from '@/assets';
import { PollCard } from '@/features/post-editor/components/poll/PollCard';
import { hasUserVoted } from '@/features/post-editor/utils/poll';
import { PollResults } from '@/features/posts/components/detail/poll/PollResults';
import { PollVoting } from '@/features/posts/components/detail/poll/PollVoting';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { FlipView } from '@/shared/components/animated/FlipView';
import { sleep } from '@/shared/utils/async';
import { dayjs } from '@/time';

const Mode = {
  VOTE: 'vote',
  RESULTS: 'results'
} as const;
type Mode = (typeof Mode)[keyof typeof Mode];

type Props = {
  postId: string;
  poll: ClientPoll;
};

export const PollView: FC<Props> = ({ postId, poll }) => {
  const { t } = useTranslation(LocalizationNameSpace.POSTS, {
    keyPrefix: 'components.poll'
  });

  const isExpired = dayjs().isAfter(poll?.expirationTime);
  const hasVoted = hasUserVoted(poll);

  const isFlipped = useSharedValue<boolean>(isExpired || hasVoted);
  const [viewMode, setViewMode] = useState<Mode>(
    isExpired || hasVoted ? Mode.RESULTS : Mode.VOTE
  );

  const toggleViewMode = useCallback(async () => {
    isFlipped.set(!isFlipped.value);
    await sleep(250);
    setViewMode((prev) => (prev === Mode.VOTE ? Mode.RESULTS : Mode.VOTE));
  }, [isFlipped]);

  const footer = useMemo(
    () =>
      !isExpired && (
        <View style={{ alignItems: 'center' }}>
          <Button
            icon={({ color, size }) =>
              viewMode === Mode.RESULTS ? (
                <ArrowBackIcon fill={color} width={size} height={size} />
              ) : (
                <BarChartIcon fill={color} width={size} height={size} />
              )
            }
            onPress={toggleViewMode}
            mode="text"
          >
            {t(viewMode, { voterCount: poll?.voterCount ?? 0 })}
          </Button>
        </View>
      ),
    [isExpired, poll?.voterCount, t, toggleViewMode, viewMode]
  );

  return (
    <FlipView
      isFlipped={isFlipped}
      front={
        <PollCard poll={poll}>
          <PollVoting postId={postId} poll={poll} onVote={toggleViewMode} />
          {footer}
        </PollCard>
      }
      back={
        <PollCard poll={poll}>
          <PollResults poll={poll} />
          {footer}
        </PollCard>
      }
    />
  );
};
